import {
  Alert,
  Button,
  Card,
  Checkbox,
  Code,
  Group,
  Modal,
  NativeSelect,
  Stack,
  Text,
  TextInput,
  Textarea,
  Title,
} from '@mantine/core';
import { IconAlertTriangle, IconCheck } from '@tabler/icons-react';
import { useState } from 'react';
import { ApiError } from '../api/client';
import { useExecuteErasure, usePreviewErasure, type ErasurePreview } from '../api/erasure';
import { useMe } from '../api/session';

type ErasureMode = 'delete' | 'pseudonymise';

export function PatientErasurePage() {
  const me = useMe();

  const [orgId, setOrgId] = useState('');
  const [patientId, setPatientId] = useState('');
  const [preview, setPreview] = useState<ErasurePreview | null>(null);
  const [mode, setMode] = useState<ErasureMode>('pseudonymise');
  const [typedRef, setTypedRef] = useState('');
  const [override, setOverride] = useState(false);
  const [reason, setReason] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  // Holds the just-completed action so we can show a confirmation banner
  // after the form has been cleared.
  const [done, setDone] = useState<{ mode: ErasureMode; patientId: string } | null>(null);

  const previewMut = usePreviewErasure(patientId);
  const executeMut = useExecuteErasure(patientId);

  // Only super_admin may operate this page. Wait for the session to
  // resolve before deciding, so we don't flash the "not authorised"
  // alert while `/api/me` is still loading.
  if (me.data && me.data.role !== 'super_admin') {
    return (
      <Alert color="red" icon={<IconAlertTriangle size={18} />}>
        Not authorised. super_admin role required.
      </Alert>
    );
  }

  const withinWindow = preview?.retention.within_window ?? false;
  const holdActive = preview?.legal_hold.active ?? false;
  const canExecute =
    !!preview &&
    !holdActive &&
    typedRef === patientId &&
    reason.trim().length > 0 &&
    (!withinWindow || override);

  const handlePreview = () => {
    // Re-running preview invalidates the prior confirmation state so a
    // stale typed ref / override can't carry over to a different patient,
    // and clears any prior completion banner.
    setPreview(null);
    setTypedRef('');
    setOverride(false);
    setDone(null);
    previewMut.mutate(orgId.trim(), { onSuccess: (p) => setPreview(p) });
  };

  // Final step: runs after the operator confirms in the modal. On success we
  // clear every control and surface a persistent completion banner.
  const handleConfirmExecute = () => {
    executeMut.mutate(
      {
        organisation_id: orgId.trim(),
        mode,
        typedPatientRef: typedRef,
        reason: reason.trim(),
        overrideRetention: override,
      },
      {
        onSuccess: () => {
          setDone({ mode, patientId });
          setConfirmOpen(false);
          setOrgId('');
          setPatientId('');
          setPreview(null);
          setTypedRef('');
          setOverride(false);
          setReason('');
          setMode('pseudonymise');
        },
        // Keep the form intact on failure so the operator can retry; close the
        // modal so the inline error alert is visible.
        onError: () => setConfirmOpen(false),
      },
    );
  };

  return (
    <Stack gap="lg">
      <Title order={2}>Patient erasure &amp; pseudonymisation</Title>

      {done && (
        <Alert color="green" icon={<IconCheck size={18} />} title="Erasure complete">
          The {done.mode} request for patient <Code>{done.patientId}</Code> was accepted and
          audit-logged.
        </Alert>
      )}

      <Card shadow="sm" padding="lg" maw={720}>
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            Look up a patient to preview the impact, then delete or pseudonymise their record.
            Actions are irreversible and audit-logged.
          </Text>
          <TextInput
            label="Organisation ID"
            required
            value={orgId}
            onChange={(e) => setOrgId(e.currentTarget.value)}
            disabled={previewMut.isPending}
          />
          <TextInput
            label="Patient ID"
            required
            value={patientId}
            onChange={(e) => setPatientId(e.currentTarget.value)}
            disabled={previewMut.isPending}
          />
          <Group justify="flex-end">
            <Button
              onClick={handlePreview}
              disabled={!orgId.trim() || !patientId.trim()}
              loading={previewMut.isPending}
            >
              Preview impact
            </Button>
          </Group>
          {previewMut.isError && (
            <Alert color="red" icon={<IconAlertTriangle size={18} />}>
              {formatError(previewMut.error)}
            </Alert>
          )}
        </Stack>
      </Card>

      {preview && (
        <Card shadow="sm" padding="lg" maw={720}>
          <Stack gap="md">
            <Text>
              Cases: <strong>{preview.counts.cases}</strong> &middot; Findings:{' '}
              <strong>{preview.counts.findings}</strong> &middot; Status:{' '}
              <strong>{preview.status}</strong>
            </Text>

            {holdActive && (
              <Alert color="red" icon={<IconAlertTriangle size={18} />}>
                Legal hold active{preview.legal_hold.reason ? `: ${preview.legal_hold.reason}` : ''}
                . Erasure is blocked.
              </Alert>
            )}

            {withinWindow && (
              <Alert color="orange" icon={<IconAlertTriangle size={18} />}>
                Within retention window
                {preview.retention.window_ends_at
                  ? ` (ends ${preview.retention.window_ends_at})`
                  : ''}
                . Override and a reason are required to proceed.
              </Alert>
            )}

            <NativeSelect
              label="Operation"
              data={[
                { value: 'pseudonymise', label: 'pseudonymise' },
                { value: 'delete', label: 'delete' },
              ]}
              value={mode}
              onChange={(e) => setMode(e.currentTarget.value as 'delete' | 'pseudonymise')}
              disabled={holdActive || executeMut.isPending}
            />

            <TextInput
              label={`Type the patient ID to confirm (${patientId})`}
              value={typedRef}
              onChange={(e) => setTypedRef(e.currentTarget.value)}
              disabled={holdActive || executeMut.isPending}
            />

            {withinWindow && (
              <Checkbox
                label="I understand and override the retention window"
                checked={override}
                onChange={(e) => setOverride(e.currentTarget.checked)}
                disabled={holdActive || executeMut.isPending}
              />
            )}

            <Textarea
              label="Reason (required, logged)"
              required
              value={reason}
              onChange={(e) => setReason(e.currentTarget.value)}
              disabled={holdActive || executeMut.isPending}
            />

            <Group justify="flex-end">
              <Button
                color="red"
                disabled={!canExecute}
                loading={executeMut.isPending}
                onClick={() => setConfirmOpen(true)}
              >
                Execute {mode}
              </Button>
            </Group>

            {executeMut.isError && (
              <Alert color="red" icon={<IconAlertTriangle size={18} />}>
                {formatError(executeMut.error)}
              </Alert>
            )}
          </Stack>
        </Card>
      )}

      <Modal
        opened={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title="Confirm irreversible action"
        centered
      >
        <Stack gap="md">
          <Alert
            color={mode === 'delete' ? 'red' : 'orange'}
            icon={<IconAlertTriangle size={18} />}
          >
            This will {mode === 'delete' ? 'permanently delete' : 'pseudonymise'} patient{' '}
            <Code>{patientId}</Code> in organisation <Code>{orgId.trim()}</Code> and fan out to all
            services. This cannot be undone.
          </Alert>
          <Text size="sm">
            <strong>Operation:</strong> {mode}
          </Text>
          <Text size="sm">
            <strong>Reason:</strong> {reason.trim()}
          </Text>
          {withinWindow && (
            <Text size="sm" c="orange">
              <strong>The retention window will be overridden.</strong>
            </Text>
          )}
          <Group justify="flex-end">
            <Button
              variant="default"
              onClick={() => setConfirmOpen(false)}
              disabled={executeMut.isPending}
            >
              Cancel
            </Button>
            <Button color="red" loading={executeMut.isPending} onClick={handleConfirmExecute}>
              Confirm {mode}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}

function formatError(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 404) return 'No patient found for that organisation and patient ID.';
    if (err.status === 409) return 'Erasure is blocked (legal hold or retention window).';
    if (err.status === 400) return 'Validation failed. Check the confirmation and reason fields.';
    if (err.status === 401) return 'Your session has expired. Please sign in again.';
    if (err.status === 403) return 'Your account does not have permission for this action.';
  }
  return 'The request failed. Please try again.';
}
